/**
 * The one helper for private files in our Storage bucket: vehicle and staff documents, children's/staff/user/
 * vehicle photos and notification images and attachments (only logos and config/ are public,
 * firebase-functions storageAccess.ts). Same module as development's services/storageLinks.ts, minus the
 * osint certificates (vehicleInfo.vue signs those itself).
 *
 * The DB keeps the plain Storage URL (the file's identity). Right before a file is shown, fetched or opened,
 * it is traded for a signed link via POST /files/sign, which checks the caller's own Hasura access
 * (firebase-functions handlers/storageLink.ts). Requests made in the same tick go out as one batch.
 *
 * - `signedUrl(url)`: synchronous and reactive, for templates (also `$img(url)`). The signed link once
 *   known, undefined while it loads (avatars show initials) or if the caller may not see it.
 * - `resolveUrl(url)`: the same as a promise, for fetches (PDF anexos, re-uploads).
 * - `resolveUrlOrThrow(url)`: same, but a refusal throws StorageLinkError with a Spanish message.
 * - `openUrl(url, toast)`: opens it in a new tab (inside the click, so popup blockers allow it).
 * - `resolveUrlsInHtml(html)`: rewrites the files inside notification HTML.
 * Anything else (logos, data:, blob:, other hosts) passes through unchanged.
 */
import { reactive } from 'vue'

const BUCKET_URL = 'https://storage.googleapis.com/buseticas-7f9fd.appspot.com/'
/** Mirrors the kinds handlers/storageLink.ts signs. */
const PRIVATE_PATH = new RegExp(
  '^(?:customers/[^/]*/(?:staff|vehicles)/[^/]+/documents/' +
    '|customers/[^/]*/instances/[^/]*/(?:avatar\\d*|notifications|attachments)/' +
    '|customers/[^/]*/staff/[^/]*/avatar/' +
    '|customers/[^/]*/vehicles/[^/]*/photos/' +
    '|users/(?:[^/]*/)?avatar/)',
)
const BATCH_MAX = 200
/** Renew a cached link this long before it expires (documents get 5-minute links, so they are never reused). */
const RENEW_MARGIN_MS = 10 * 60 * 1000
/** After a refusal or a failure, ask again only after this long. */
const RETRY_MS = 5 * 60 * 1000

export class StorageLinkError extends Error {
  constructor(message: string, readonly status: number | null = null) {
    super(message)
    this.name = 'StorageLinkError'
  }
}

interface Entry { url: string | null; expiresAt: number; status: number | null }
const links = reactive(new Map<string, Entry>())
const queued = new Set<string>()
const inFlight = new Map<string, Promise<Entry>>()
let waiters: Array<() => void> = []
let scheduled = false

/** True for a URL of our bucket that is private and needs a signed link to load. */
export function isPrivateStorageUrl(url: unknown): url is string {
  if (typeof url !== 'string' || !url.startsWith(BUCKET_URL)) return false
  try {
    return PRIVATE_PATH.test(decodeURIComponent(url.slice(BUCKET_URL.length).split('?')[0]))
  } catch {
    return false
  }
}

const usable = (e: Entry | undefined): e is Entry =>
  !!e && (e.url ? e.expiresAt - Date.now() > RENEW_MARGIN_MS : e.expiresAt > Date.now())

async function flush() {
  scheduled = false
  const urls = [...queued]
  queued.clear()
  const done = waiters
  waiters = []
  // Lazy: global axios carries the API base URL and the Firebase token (main.ts interceptor).
  const { default: axios } = await import('axios')
  for (let i = 0; i < urls.length; i += BATCH_MAX) {
    const chunk = urls.slice(i, i + BATCH_MAX)
    try {
      const { data } = await axios.post('files/sign', { urls: chunk })
      for (const u of chunk) {
        const l = data?.links?.[u]
        links.set(u, l?.url
          ? { url: l.url, expiresAt: Number(l.expiresAt) || Date.now() + RETRY_MS, status: 200 }
          : { url: null, expiresAt: Date.now() + RETRY_MS, status: 403 })
      }
    } catch (err: any) {
      console.warn('[storageLinks] sign failed', err)
      const status = err?.response?.status ?? null
      for (const u of chunk) links.set(u, { url: null, expiresAt: Date.now() + RETRY_MS, status })
    }
  }
  done.forEach((resolve) => resolve())
}

function fetchEntry(url: string): Promise<Entry> {
  let p = inFlight.get(url)
  if (!p) {
    queued.add(url)
    const wait = new Promise<void>((resolve) => waiters.push(resolve))
    if (!scheduled) {
      scheduled = true
      setTimeout(flush, 0)
    }
    p = wait.then(() => {
      inFlight.delete(url)
      return links.get(url) ?? { url: null, expiresAt: 0, status: null }
    })
    inFlight.set(url, p)
  }
  return p
}

/** Resolves to a URL that loads right now (signed when private), or null when the caller may not see it. */
export async function resolveUrl(url: string | null | undefined): Promise<string | null> {
  if (!url) return null
  if (!isPrivateStorageUrl(url)) return url
  const e = links.get(url)
  return (usable(e) ? e : await fetchEntry(url)).url
}

/** Reactive, synchronous (templates, `$img`): the signed link once known, else undefined. */
export function signedUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  if (!isPrivateStorageUrl(url)) return url
  const e = links.get(url)
  if (!usable(e)) void fetchEntry(url)
  return e?.url ?? undefined
}

function messageForStatus(status: number | null): string {
  if (status === 401) return 'Tu sesión expiró. Recarga la página e inténtalo de nuevo.'
  if (status === 403) return 'No tienes permiso para ver este documento.'
  if (status === 429) return 'Demasiadas solicitudes. Intenta de nuevo en un minuto.'
  if (status) return 'El servicio de documentos no respondió. Intenta de nuevo.'
  return 'No se pudo conectar con el servicio de documentos.'
}

/** URLs that must be traded for a signed one before opening/fetching. */
export function needsSignedLink(url: string | null | undefined): boolean {
  return isPrivateStorageUrl(url)
}

/** Like resolveUrl, but a refusal or failure throws StorageLinkError with a Spanish message. */
export async function resolveUrlOrThrow(url: string): Promise<string> {
  if (!isPrivateStorageUrl(url)) return url
  const e = links.get(url)
  const entry = usable(e) ? e : await fetchEntry(url)
  if (entry.url) return entry.url
  // A refusal is remembered for a while; a document the caller just gained access to can retry right away.
  links.delete(url)
  throw new StorageLinkError(messageForStatus(entry.status), entry.status)
}

export interface StorageLinkToast {
  add: (opts: { title: string; description?: string; color?: 'error' }) => void
}

/**
 * Opens a file in a new tab. A private one gets its tab synchronously (still inside the click, so popup
 * blockers allow it), pointed at the signed URL once it arrives; on failure the tab closes and a toast says why.
 */
export async function openUrl(url: string, toast?: StorageLinkToast): Promise<void> {
  if (!needsSignedLink(url)) {
    window.open(url, '_blank')
    return
  }
  const tab = window.open('', '_blank')
  try {
    const signed = await resolveUrlOrThrow(url)
    if (tab && !tab.closed) {
      tab.opener = null
      tab.location.href = signed
    } else {
      window.open(signed, '_blank')
    }
  } catch (error) {
    tab?.close()
    const description = error instanceof StorageLinkError ? error.message : 'No se pudo abrir el documento.'
    console.warn('[storageLinks] signed link failed', error)
    toast?.add({ title: 'No se pudo abrir el documento', description, color: 'error' })
  }
}

/** Rewrites every private file URL inside an HTML string (notification content) to its signed link. */
export async function resolveUrlsInHtml(html: string | null | undefined): Promise<string> {
  if (!html) return ''
  const found = new Set<string>()
  for (const m of html.matchAll(/https:\/\/storage\.googleapis\.com\/buseticas-7f9fd\.appspot\.com\/[^"'\s)<>]+/g)) {
    if (isPrivateStorageUrl(m[0].replace(/&amp;/g, '&'))) found.add(m[0])
  }
  if (!found.size) return html
  const pairs = await Promise.all([...found].map(async (u) => [u, await resolveUrl(u.replace(/&amp;/g, '&'))] as const))
  let out = html
  for (const [u, s] of pairs) out = out.split(u).join(s ? s.replace(/&/g, '&amp;') : '')
  return out
}

/** Tests only. */
export function _resetStorageLinks() {
  links.clear()
  queued.clear()
  inFlight.clear()
  waiters = []
  scheduled = false
}
